import { useState } from 'react';
import { DIAGNOSTIC_ROLES, type DiagnosticApp, type DiagnosticResult, type DiagnosticRole } from '@shared/prospect-diagnostic';
import { DiagnosticAppDrawer } from '@/components/diagnostic/DiagnosticAppDrawer';

const roleLabel = (key: string) => DIAGNOSTIC_ROLES.find((role) => role.key === key)?.label ?? key;

interface PlacedApp { app: DiagnosticApp; role: DiagnosticRole }
interface Block { title: string; apps: PlacedApp[] }

/**
 * The page by what the prospect wants (Benoit, 03/11): one block per aim, each app once, in the
 * order the apps hand over. A diagnostic written before prompt v9 has no aims: one block per role.
 */
const blocksOf = (result: DiagnosticResult): Block[] => {
  const byName = new Map(result.roles.flatMap((plan) => plan.apps.map((app) => [app.name, { app, role: plan.role }] as const)));
  if (result.aims?.length) {
    return result.aims.map((aim) => ({
      title: aim.title,
      apps: aim.apps.flatMap((name) => { const placed = byName.get(name); return placed ? [placed] : []; }),
    })).filter((block) => block.apps.length > 0);
  }
  return result.roles
    .map((plan) => ({ title: plan.headline || roleLabel(plan.role), apps: plan.apps.map((app) => ({ app, role: plan.role })) }))
    .filter((block) => block.apps.length > 0);
};

/** One app as a light card: who it is for, its name, what it gives; the rest in a drawer. */
const AppCard = ({ placed, onOpen }: { placed: PlacedApp; onOpen: () => void }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group flex min-h-[112px] flex-col gap-1.5 rounded-2xl border-2 border-domino-sand bg-white p-4 text-left transition-[border-color,transform] duration-150 ease-snap active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-domino-prune [@media(hover:hover)]:hover:border-domino-aubergine/40"
  >
    <span className="flex items-start justify-between gap-3">
      <span className="text-[17px] font-semibold leading-snug text-domino-aubergine">{placed.app.name}</span>
      <svg width="14" height="14" viewBox="0 0 12 12" aria-hidden className="mt-1.5 flex-none text-domino-inkSoft">
        <path d="M4 2l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className="line-clamp-2 text-[15px] text-domino-inkSoft">{placed.app.produces}</span>
    <span className="mt-auto pt-1 text-[13px] font-semibold uppercase tracking-wide text-domino-inkSoft/80">{roleLabel(placed.role)}</span>
  </button>
);

export const DiagnosticAims = ({ result }: { result: DiagnosticResult }) => {
  const [opened, setOpened] = useState<PlacedApp | null>(null);
  return (
    <div className="flex flex-col gap-10">
      {blocksOf(result).map((block) => (
        <section key={block.title} className="flex flex-col gap-4">
          <h2 className="font-display text-2xl font-semibold leading-snug text-domino-aubergine">{block.title}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {block.apps.map((placed) => <AppCard key={placed.app.name} placed={placed} onOpen={() => setOpened(placed)} />)}
          </div>
        </section>
      ))}
      {opened && <DiagnosticAppDrawer app={opened.app} role={roleLabel(opened.role)} onClose={() => setOpened(null)} />}
    </div>
  );
};
